import React from 'react'
import { useTranslation } from 'react-i18next'

import { IconS24Crane224 } from '@is24/cosma-ui-icons'
import { TooltipTypes } from '../../../../../@types/types'
import { ConstructionScreenValidationResult } from '../../../../../@types/validation-types'
import { InsertionFlowScreensProps } from '../../../../../@types/props-types'
import EnergyCertificate from './energyCertificate/EnergyCertificate'
import { SCREEN_CONSTRUCTION, SCREENS } from '../../../../../services/domain/wizardScreens'
import ScreenContainer from '../../../../common/containers/screenContainer/ScreenContainer'
import { ScreenLayout } from '../../../../common/containers/screenLayout/ScreenLayout'
import ConstructionConditionField from './fields/constructionConditionField/ConstructionConditionField'
import LastModernizationField from '../commonFields/lastModernizationField/LastModernizationField'
import ConstructionYearField from '../commonFields/constructionYearField/ConstructionYearField'
import HeatingTypeField from '../commonFields/heatingType/HeatingTypeField'
import { getDefaultClasses } from '../../../../../utils/style-utils'
import { ScreenTooltip } from '../../../../common/display/screenTooltip/ScreenTooltip'
import EnergySourceField from './fields/energySourceField/EnergySourceField'
import { useIsBelowDesk } from '../../../../../utils/useIsBelowDesk'

const screen = SCREENS[SCREEN_CONSTRUCTION]
type AdditionalConstructionProps = {
  hasLastModernizationField?: boolean
}
const Construction: React.FC<
  InsertionFlowScreensProps<ConstructionScreenValidationResult> & AdditionalConstructionProps
> = ({ insertionData, validationMessages, onChange, doReset, onNavigate, hasLastModernizationField }) => {
  const { t } = useTranslation()
  const isBelowDesk = useIsBelowDesk()
  const deskTooltip = (
    <ScreenTooltip tooltipType={TooltipTypes.DESKTOP} tooltipKey='tooltip.screens.construction.common' />
  )
  const leftWrapperClassName = isBelowDesk ? getDefaultClasses('full') : getDefaultClasses('left')
  const rightWrapperClassName = isBelowDesk ? getDefaultClasses('full') : getDefaultClasses('right')

  return (
    <ScreenContainer containerId={screen.id} onNavigate={onNavigate} screen={screen} className=''>
      <ScreenLayout
        id={`${screen.id}-desk-tooltip`}
        headline={'wizard.screen.construction.header'}
        deskTooltip={deskTooltip}
        className='grid gutter-vertical-l'
        headerIcon={<IconS24Crane224 />}
        screenName={screen.name}
      >
        <ConstructionConditionField
          insertionData={insertionData}
          onChange={onChange}
          screenName={screen.name}
          showLabelWithTooltip={isBelowDesk}
          wrapperClassName={leftWrapperClassName}
        />

        {hasLastModernizationField && (
          <LastModernizationField
            insertionData={insertionData}
            validationMessages={validationMessages}
            onChange={onChange}
            screenName={screen.name}
            showLabelWithTooltip={isBelowDesk}
            wrapperClassName={rightWrapperClassName}
          />
        )}
        <HeatingTypeField
          insertionData={insertionData}
          screenName={SCREENS[SCREEN_CONSTRUCTION].name}
          onChange={onChange}
          wrapperClassName={hasLastModernizationField ? leftWrapperClassName : rightWrapperClassName}
        />
        <EnergySourceField
          insertionData={insertionData}
          onChange={onChange}
          screenName={screen.name}
          wrapperClassName={hasLastModernizationField ? rightWrapperClassName : leftWrapperClassName}
          showLabelWithTooltip={isBelowDesk}
        />
        <ConstructionYearField
          insertionData={insertionData}
          validationMessages={validationMessages}
          onChange={onChange}
          screenName={screen.name}
          wrapperClassName={hasLastModernizationField ? leftWrapperClassName : rightWrapperClassName}
        />
        <div className='grid-item one-whole'>
          <EnergyCertificate
            screenId={screen.id}
            insertionData={insertionData}
            validators={validationMessages!}
            onEnergyCertificateChange={(field, value) => onChange({ screen: screen.name, property: field }, value)}
            doReset={doReset!}
          />
        </div>
        <div className='grid-item one-whole margin-bottom-none font-default-color'>
          <span>
            {t(
              'wizard.screen.construction.hint',
              'The Building Energy Law GEG 2020 requires that all relevant information from the energy certificate must be presented. The energy certificate must be presented without being asked at the latest during the viewing.'
            )}
          </span>
        </div>
      </ScreenLayout>
    </ScreenContainer>
  )
}

export default Construction
